import React, { useState } from "react";

export default function TimeTab({ formData, onChange, disabled }) {
  const [confirmDialog, setConfirmDialog] = useState(null);
  // confirmDialog: { field: "allowMultipleEmployeeEntries" } | null

  const handleCrewRequiredUncheck = (field) => {
    setConfirmDialog({ field });
  };

  const handleConfirmProceed = () => {
    const { field } = confirmDialog;
    setConfirmDialog(null);
    onChange(field, false);
    onChange("allowCrewSetup", false);
    if (field === "allowMultipleEmployeeEntries") {
      onChange("allowSigningOtherEmployees", false);
      onChange("showMyEmployeesWeeklySummary", false);
      onChange("showMyEmployeesWorkerClass", false);
    }
  };

  const handleConfirmCancel = () => {
    setConfirmDialog(null);
  };

  const payTypes = [
    { key: "overtime", label: "Overtime", code: "O" },
    { key: "double", label: "Double", code: "D" },
    { key: "sick", label: "Sick", code: "S" },
    { key: "vacation", label: "Vacation", code: "V" },
    { key: "holiday", label: "Holiday", code: "H" },
    { key: "other", label: "Other", code: "OT" },
    { key: "nontax", label: "Nontax", code: "NT" },
  ];

  const handlePayTypeChange = (option, value) => {
    onChange("payTypeOptions", {
      ...formData.payTypeOptions,
      [option]: value,
    });
  };

  return (
    <div className="role-tab">
      {/* Enable Time */}
      <div className="role-tab__header">
        <h3 className="role-tab__title">Time Entry Configuration</h3>
        <label className="role-form__checkbox-label role-form__checkbox-label--large">
          <input
            type="checkbox"
            checked={formData.enableTime}
            onChange={(e) => onChange("enableTime", e.target.checked)}
            disabled={disabled}
          />
          <span>Enable Time</span>
        </label>
      </div>

      {formData.enableTime && (
        <div className="role-tab__content">
          {/* Multiple Employee Entries */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.allowMultipleEmployeeEntries}
              onChange={(e) => {
                const checked = e.target.checked;
                if (!checked) {
                  handleCrewRequiredUncheck("allowMultipleEmployeeEntries");
                } else {
                  onChange("allowMultipleEmployeeEntries", true);
                }
              }}
              disabled={disabled || !formData.enableTime}
            />
            <span>
              Allow Multiple Employee Entries (Required for Crew Entry)
            </span>
          </label>

          {/* Nested checkbox - Signing Other Employees */}
          <label className="role-form__checkbox-label role-form__checkbox-label--indent-1">
            <input
              type="checkbox"
              checked={formData.allowSigningOtherEmployees}
              onChange={(e) =>
                onChange("allowSigningOtherEmployees", e.target.checked)
              }
              disabled={
                disabled ||
                !formData.enableTime ||
                !formData.allowMultipleEmployeeEntries
              }
            />
            <span>Allow Signing Other Employee Entries</span>
          </label>

          {/* Nested checkbox level 2 - My Employees Weekly Summary */}
          <label className="role-form__checkbox-label role-form__checkbox-label--indent-1">
            <input
              type="checkbox"
              checked={formData.showMyEmployeesWeeklySummary}
              onChange={(e) =>
                onChange("showMyEmployeesWeeklySummary", e.target.checked)
              }
              disabled={
                disabled ||
                !formData.enableTime ||
                !formData.allowMultipleEmployeeEntries
              }
            />
            <span>Show "My Employees'" Weekly Summary</span>
          </label>

          {/* Nested checkbox level 2 - My Employees Worker Class */}
          <label className="role-form__checkbox-label role-form__checkbox-label--indent-1">
            <input
              type="checkbox"
              checked={formData.showMyEmployeesWorkerClass}
              onChange={(e) =>
                onChange("showMyEmployeesWorkerClass", e.target.checked)
              }
              disabled={
                disabled ||
                !formData.enableTime ||
                !formData.allowMultipleEmployeeEntries
              }
            />
            <span>Show "My Employees'" Worker Class</span>
          </label>

          {/* Allow Approvals */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.allowApprovals}
              onChange={(e) => {
                const checked = e.target.checked;
                onChange("allowApprovals", checked);
                if (checked) {
                  onChange("viewAndSignOnly", false);
                }
              }}
              disabled={
                disabled || !formData.enableTime || formData.viewAndSignOnly
              }
            />
            <span>Allow Approvals</span>
          </label>

          {/* Allow Crew Setup */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.allowCrewSetup}
              onChange={(e) => onChange("allowCrewSetup", e.target.checked)}
              disabled={
                disabled ||
                !formData.enableTime ||
                !formData.allowMultipleEmployeeEntries
              }
            />
            <span>
              Allow Crew Setup
              {!formData.allowMultipleEmployeeEntries && (
                <span className="role-form__checkbox-hint">
                  {" "}
                  (Requires Multiple Employee Entries)
                </span>
              )}
            </span>
          </label>

          {/* Allow Absence Entries */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.allowAbsenceEntries}
              onChange={(e) =>
                onChange("allowAbsenceEntries", e.target.checked)
              }
              disabled={disabled || !formData.enableTime}
            />
            <span>Allow Absence Entries</span>
          </label>

          {/* View and Sign Only */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.viewAndSignOnly}
              onChange={(e) => {
                const checked = e.target.checked;
                onChange("viewAndSignOnly", checked);
                if (checked) {
                  onChange("allowApprovals", false);
                }
              }}
              disabled={
                disabled || !formData.enableTime || formData.allowApprovals
              }
            />
            <span>View and Sign Only</span>
          </label>

          {/* Show Pay Type */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.showPayType}
              onChange={(e) => onChange("showPayType", e.target.checked)}
              disabled={disabled || !formData.enableTime}
            />
            <span>Show Pay Type</span>
          </label>

          {/* Pay Type Options (nested) */}
          {formData.showPayType && (
            <div className="role-form__pay-types">
              <div className="role-form__pay-types-label">
                Allow Regular time entries and
              </div>
              <div className="role-form__pay-types-grid">
                {payTypes.map((payType) => {
                  const isChecked =
                    formData.payTypeOptions[payType.key] || false;
                  return (
                    <label
                      key={payType.key}
                      className={`role-form__pay-type-badge ${
                        isChecked ? "role-form__pay-type-badge--checked" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) =>
                          handlePayTypeChange(payType.key, e.target.checked)
                        }
                        disabled={disabled || !formData.enableTime}
                      />
                      <span className="role-form__pay-type-code">
                        {payType.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Require Timer */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.requireTimer}
              onChange={(e) => onChange("requireTimer", e.target.checked)}
              disabled={disabled || !formData.enableTime}
            />
            <span>Require Timer</span>
          </label>

          {/* Require Job */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.requireJob}
              onChange={(e) => onChange("requireJob", e.target.checked)}
              disabled={disabled || !formData.enableTime}
            />
            <span>Require Job</span>
          </label>

          {/* Show Workers Class */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.showWorkersClass}
              onChange={(e) => onChange("showWorkersClass", e.target.checked)}
              disabled={disabled || !formData.enableTime}
            />
            <span>Show Workers Class</span>
          </label>

          {/* Show Department */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.showDepartment}
              onChange={(e) => onChange("showDepartment", e.target.checked)}
              disabled={disabled || !formData.enableTime}
            />
            <span>Show Department</span>
          </label>

          {/* Show Work Location */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.showWorkLocation}
              onChange={(e) => onChange("showWorkLocation", e.target.checked)}
              disabled={disabled || !formData.enableTime}
            />
            <span>Show Work Location</span>
          </label>

          {/* Show Equipment */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.showEquipment}
              onChange={(e) => onChange("showEquipment", e.target.checked)}
              disabled={disabled || !formData.enableTime}
            />
            <span>Show Equipment</span>
          </label>

          {/* Show Units */}
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.showUnits}
              onChange={(e) => onChange("showUnits", e.target.checked)}
              disabled={disabled || !formData.enableTime}
            />
            <span>Show Units</span>
          </label>

          {/* Alert Tolerance */}
          <div className="role-form__alert-tolerance">
            <h4 className="role-form__subsection-title">Alert Tolerance</h4>
            <div className="role-form__alert-tolerance-grid">
              <div className="role-form__alert-field">
                <label className="role-form__alert-label">
                  Timer Entry Alerts over
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.timerAlertMinutes}
                  onChange={(e) =>
                    onChange("timerAlertMinutes", parseInt(e.target.value) || 0)
                  }
                  disabled={disabled || !formData.enableTime}
                  className="role-form__alert-input"
                />
                <span className="role-form__alert-unit">Minutes</span>
              </div>
              <div className="role-form__alert-field">
                <label className="role-form__alert-label">
                  Hours Entry Alerts over
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={formData.hoursAlertHours}
                  onChange={(e) =>
                    onChange("hoursAlertHours", parseFloat(e.target.value) || 0)
                  }
                  disabled={disabled || !formData.enableTime}
                  className="role-form__alert-input"
                />
                <span className="role-form__alert-unit">Hours</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Crew Entry confirmation dialog */}
      {confirmDialog && (
        <div className="role-form__confirm-overlay">
          <div className="role-form__confirm-dialog">
            <h4 className="role-form__confirm-title">
              Disable Crew Time Access?
            </h4>
            <p className="role-form__confirm-message">
              Unchecking this setting will cause users to lose access to Crew
              Time. Do you want to continue?
            </p>
            <div className="role-form__confirm-actions">
              <button
                className="button button--secondary"
                onClick={handleConfirmCancel}
              >
                Cancel
              </button>
              <button
                className="button button--primary"
                onClick={handleConfirmProceed}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
